await customElements.whenDefined('zoom-dialog');
const viewer = document.getElementById('gallery-image-viewer');
const { default: sources } = await import(viewer.dataset.sourcesUrl);
const dialog = viewer.querySelector('dialog');
const enlarged = viewer.querySelector('img');
const caption = viewer.querySelector('.gallery-image-viewer__caption span');
const destination = viewer.querySelector('.gallery-image-viewer__caption a');

const responsiveImages = new ResizeObserver((entries) => {
  for (const { target } of entries) target.sizes = `${Math.ceil(target.getBoundingClientRect().width || window.innerWidth)}px`;
});

function fullImageUrl(image) {
  const url = new URL(image.getAttribute('data-gallery-full-src') || image.src, location.href);
  if (url.pathname.includes('/cdn/shop/files/') || url.hostname === 'cdn.shopify.com') {
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    url.searchParams.set('width', '5760');
  }
  return url.href;
}

function openImage(image, event) {
  const fallback = image.currentSrc || image.src;
  enlarged.alt = image.alt;
  delete enlarged.dataset.viewerError;
  enlarged.removeAttribute('srcset');
  enlarged.width = Number(image.getAttribute('width')) || image.naturalWidth;
  enlarged.height = Number(image.getAttribute('height')) || image.naturalHeight;
  enlarged.setAttribute('data_max_resolution', fullImageUrl(image));
  enlarged.src = fallback;
  caption.textContent = image.alt;
  const link = image.closest('a[href]');
  destination.hidden = !link;
  if (link) destination.href = link.href;
  dialog.setAttribute('aria-label', image.alt ? `Enlarged photograph: ${image.alt}` : 'Enlarged photograph');
  viewer.open(0, event);
}

function prepareImage(image) {
  if (image.hasAttribute('data-gallery-enlarge') || image.hasAttribute('data-gallery-fallback-used') || image.matches('.hero__blurred-image')) return;
  if (image.closest('media-gallery, product-card, dialog, button')) return;
  if (image.closest('.artsy-artwork-strip') && !image.hasAttribute('data-gallery-fallback')) {
    // A failed image upgrade must not leave a broken tile in the moving strip.
    const fallback = image.currentSrc || image.src;
    image.setAttribute('data-gallery-fallback', '');
    image.addEventListener('error', () => {
      image.setAttribute('data-gallery-fallback-used', '');
      responsiveImages.unobserve(image);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = fallback;
    }, { once: true });
  }
  const url = new URL(image.src, location.href);
  const original = url.hostname === 'd7hftxdivxxvm.cloudfront.net'
    ? new URL(url.searchParams.get('src') || image.src, location.href) : url;
  const source = (original.hostname === 'd32dm0rphc51dk.cloudfront.net' && sources[original.pathname.split('/')[1]])
    || (url.pathname.includes('/files/') && sources[url.pathname.split('/').pop()]);
  if (source && !image.hasAttribute('data-gallery-source')) {
    image.setAttribute('data-gallery-source', '');
    const sourceUrl = source.asset ? new URL(source.asset, import.meta.url).href : source.url;
    if (source.asset) {
      image.setAttribute('data-gallery-full-src', sourceUrl);
      const widths = [320, 640, 960, 1280, 1920, 2560, 3840, source.width].filter((width, index, all) => width <= source.width && all.indexOf(width) === index);
      image.srcset = widths.map((width) => {
        const variant = new URL(sourceUrl);
        variant.pathname = variant.pathname.replace(/\.jpg$/, `_${width}x.jpg`);
        return `${variant.href} ${width}w`;
      }).join(', ');
      image.src = sourceUrl;
    } else if (source.external) {
      image.removeAttribute('srcset');
      image.src = sourceUrl;
    } else {
      const widths = [320, 640, 960, 1280, 1920, 2560, 3840, 5760].filter((width) => width < source.width);
      widths.push(source.width);
      image.srcset = widths.map((width) => `${sourceUrl}?width=${width} ${width}w`).join(', ');
      image.sizes = `${Math.ceil(image.getBoundingClientRect().width || window.innerWidth)}px`;
      responsiveImages.observe(image);
      image.src = `${sourceUrl}?width=1920`;
    }
  }
  if (image.closest('.artsy-artwork-strip') && !image.srcset && url.hostname === 'cdn.shopify.com' && url.pathname.includes('/files/')) {
    image.srcset = [320, 480, 640, 960].map((width) => {
      const variant = new URL(image.src, location.href);
      variant.searchParams.set('width', String(width));
      return `${variant.href} ${width}w`;
    }).join(', ');
    image.sizes = `${Math.ceil(image.getBoundingClientRect().width || window.innerWidth)}px`;
    responsiveImages.observe(image);
  }
  if (image.hasAttribute('data-gallery-full-src') && !image.hasAttribute('data-gallery-sized')) {
    image.setAttribute('data-gallery-sized', '');
    if (!image.matches('.hero__media')) {
      image.sizes = `${Math.ceil(image.getBoundingClientRect().width || window.innerWidth)}px`;
      responsiveImages.observe(image);
    }
  }
  if (image.closest('[aria-hidden="true"]')) return;
  // Linked images retain their existing navigation.
  if (image.closest('a[href]')) return;
  if (/logo|icon|signature/i.test(image.className + ' ' + image.alt)) return;
  const width = Number(image.getAttribute('width')) || image.naturalWidth;
  const height = Number(image.getAttribute('height')) || image.naturalHeight;
  if (width < 160 || height < 100) return;
  image.setAttribute('data-gallery-enlarge', '');
  const hero = image.closest('.hero');
  if (hero) {
    let button = hero.querySelector('.gallery-image-enlarge');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'gallery-image-enlarge';
      button.textContent = 'Enlarge image';
      hero.append(button);
    }
    button.setAttribute('aria-haspopup', 'dialog');
  } else {
    image.tabIndex = 0;
    image.setAttribute('role', 'button');
    image.setAttribute('aria-label', `Enlarge image${image.alt ? ': ' + image.alt : ''}`);
    image.setAttribute('aria-haspopup', 'dialog');
  }
}

function prepareImages() {
  document.querySelectorAll('main img, footer img').forEach(prepareImage);
}

function imageForTarget(target) {
  const button = target.closest('.gallery-image-enlarge');
  if (button) return [...button.closest('.hero').querySelectorAll('img[data-gallery-enlarge]')].find((image) => image.getBoundingClientRect().width > 0);
  if (target.matches('img[data-gallery-enlarge]')) return target;
  return null;
}

document.addEventListener('click', (event) => {
  if (event.target.closest('a[href]')) return;
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const image = imageForTarget(event.target);
  if (image) openImage(image, event);
});
document.addEventListener('keydown', (event) => {
  if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('img[data-gallery-enlarge]')) {
    openImage(event.target, event);
  }
});
prepareImages();
document.addEventListener('shopify:section:load', () => prepareImages());

document.addEventListener('load', (event) => {
  if (event.target instanceof HTMLImageElement && event.target.closest('main, footer')) prepareImage(event.target);
}, true);
